import { Alert, EmptyState, PageHeader, Pagination, Skeleton } from '@ntconsult/aluma-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExportUnallocated } from '../hooks/useExportUnallocated';
import { usePaginationLabels } from '../hooks/usePaginationLabels';
import {
  UNALLOCATED_COLUMN_LABEL_KEYS,
  useUnallocatedColumns,
} from '../hooks/useUnallocatedColumns';
import { useUnallocatedContracts } from '../hooks/useUnallocatedContracts';
import { useUnallocatedFilters } from '../hooks/useUnallocatedFilters';
import { formatDisplayDate } from '../utils/data';
import { i18nKeyForError, isSessionError, parseApiError } from '../utils/errors';
import { CheckToolbar } from './CheckToolbar';
import { ColumnsPicker } from './ColumnsPicker';
import { ExportButton } from './ExportButton';
import { NoIdentityState } from './NoIdentityState';
import { PerPageSelect } from './PerPageSelect';
import { UnallocatedFilterChips } from './UnallocatedFilterChips';
import { UnallocatedFiltersDrawer } from './UnallocatedFiltersDrawer';
import { UnallocatedTable } from './UnallocatedTable';

/**
 * Consulta "Sem Alocação" do Axiis legado (Operação → Consultas): contratos
 * ativos, em experiência ou em contratação sem alocação vigente no dia. Uma
 * linha por contrato, como no legado.
 */
export function UnallocatedPage() {
  const { t, i18n } = useTranslation('alocacao');
  const paginationLabels = usePaginationLabels();
  const filterState = useUnallocatedFilters();
  const query = useUnallocatedContracts(filterState.filters);
  const columnsState = useUnallocatedColumns();
  const exportUnallocated = useExportUnallocated(filterState.filters, columnsState.visibleColumns);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const { data, isLoading, isError } = query;
  const error = parseApiError(query.error);
  const isListLoading = isLoading || query.isPlaceholderData;

  if (isError && error && isSessionError(error)) return <NoIdentityState code={error.code} />;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.perPage)) : 1;
  // Sem filtro e sem ninguém, a checagem passou: o estado vazio substitui a tabela.
  const allAllocated = data && data.total === 0 && !filterState.hasActiveFilters;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('consultas.semAlocacao.titulo')}
        description={t('consultas.semAlocacao.descricao')}
      />

      <section
        className="flex min-w-0 flex-col gap-4"
        aria-label={t('consultas.semAlocacao.titulo')}
      >
        <CheckToolbar
          date={filterState.date}
          appliedDate={data?.date}
          onDateChange={filterState.setDate}
          dateHint={t('consultas.semAlocacao.dataReferenciaDica')}
          onOpenFilters={() => setAdvancedOpen(true)}
          actions={
            <>
              <ColumnsPicker
                columnsState={columnsState}
                labelKeys={UNALLOCATED_COLUMN_LABEL_KEYS}
              />
              <ExportButton
                exportMutation={exportUnallocated}
                disabled={!data?.total}
                labels={{
                  title: t('consultas.semAlocacao.exportarTitulo'),
                  allPages: t('exportacao.todasPaginasRegistros', { count: data?.total ?? 0 }),
                  currentPage: t('exportacao.paginaAtualRegistros', {
                    count: data?.items.length ?? 0,
                  }),
                }}
              />
            </>
          }
        />

        <UnallocatedFilterChips filterState={filterState} filterOptions={data?.filterOptions} />

        {!isListLoading && data && (
          <p className="text-sm text-foreground-muted" aria-live="polite">
            {t('consultas.semAlocacao.total', { count: data.total, showing: data.items.length })}
          </p>
        )}

        {isListLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {isError && (
          <Alert
            variant="danger"
            title={t((error ? i18nKeyForError(error.code) : 'erros.unknown') as never)}
          />
        )}

        {!isListLoading && !isError && allAllocated && (
          <EmptyState
            title={t('consultas.semAlocacao.vazioTitulo', {
              date: formatDisplayDate(data.date, i18n.language),
            })}
            description={t('consultas.semAlocacao.vazioDescricao')}
          />
        )}

        {!isListLoading && !isError && data && !allAllocated && (
          <div className="flex flex-col gap-4">
            <UnallocatedTable
              items={data.items}
              filterOptions={data.filterOptions}
              filterState={filterState}
              columnsState={columnsState}
            />
            <div className="flex flex-wrap items-center gap-4">
              <PerPageSelect value={filterState.perPage} onChange={filterState.setPerPage} />
              {totalPages > 1 && (
                <Pagination
                  {...paginationLabels}
                  page={filterState.filters.page}
                  totalPages={totalPages}
                  onPageChange={filterState.goToPage}
                />
              )}
            </div>
          </div>
        )}
      </section>

      <UnallocatedFiltersDrawer
        open={advancedOpen}
        onClose={() => setAdvancedOpen(false)}
        filterState={filterState}
        filterOptions={data?.filterOptions}
      />
    </div>
  );
}
